import { toast } from "react-toastify";
import { GD } from "src/GD";
import { getFriendlyErrorMessage } from "src/utils/errors";

export class CompanyService {
	constructor() {
		GD.REQ_COMPANIES.listener = this.getCompanies;
		GD.REQ_COMPANY_GET.listener = this.getCompany;
		GD.REQ_COMPANY_ISSUERS.listener = this.getCompanyIssuers;
		GD.REQ_COMPANY_REFERRAL.listener = this.getCompanyReferral;
		GD.REQ_COMPANY_REGISTER.listener = this.registerCompany;
		GD.REQ_COMPANY_UPDATE.listener = this.updateCompany;
		GD.REQ_COMPANY_TEMPLATE.listener = this.getCompanyTemplate;
		GD.REQ_COMPANY_TOPUP_TEMPLATE.listener = this.getCompanyTopupTemplate;
		GD.REQ_COMPANY_TOPUP.listener = this.topUpCompany;
		GD.REQ_COMPANY_USER_DISABLE.listener = this.disableUserForCompany;
	}

	private getCompanyReferral: () => Promise<IAPIResponse<ICompanyReferralResponse>> = async () =>
		(await GD.REQ_API.request({
			httpMethod: "POST",
			method: "companies/referral",
			data: {},
		})) as IAPIResponse<ICompanyReferralResponse>;

	private getCompanyIssuers: (
		options: void | { with_buyers?: boolean }
	) => Promise<IAPIResponse<ICompanyIssuer[]>> = async options =>
		(await GD.REQ_API.request({
			httpMethod: "POST",
			method: "companies/issuers",
			data: { with_buyers: !!options && options.with_buyers === true },
		})) as IAPIResponse<ICompanyIssuer[]>;

	private disableUserForCompany: (data: { user_uid: string }) => Promise<IAPIResponse<{ status: boolean }>> = async data => {
		const res = await GD.REQ_API.request({
			method: "companies/user/disable",
			httpMethod: "POST",
			data,
		});

		return res as IAPIResponse<{ status: boolean }>;
	};

	private getCompanyTopupTemplate: () => Promise<IAPIResponse<any>> = async () => {
		const res = await GD.REQ_API.request({
			method: "companies/topup/get?uid=-1",
			httpMethod: "GET",
		});

		return res;
	};

	private topUpCompany: (data: { amount: number; currency: string }) => Promise<IAPIResponse<IStatusResponse>> = async data => {
		const res = (await GD.REQ_API.request({
			method: "companies/topup",
			httpMethod: "POST",
			data,
		})) as IAPIResponse<IStatusResponse>;

		if (res.error) {
			toast.error(getFriendlyErrorMessage(res.error, "Failed to top up company"));
			return res;
		}

		if (!res.error) {
			const [companies] = await Promise.all([this.getCompanies(), GD.REQ_COMPANY_REFRESH.request()]);
			if (companies.data) GD.S_COMPANY_LIST_UPDATED.invoke(companies.data);
			GD.S_COMPANY_ISSUERS_REFRESH.invoke();
		}

		return res;
	};

	private getCompanies: (options?: { force?: boolean }) => Promise<IAPIResponse<ICompanyRaw[]>> = async _options => {
		return (await GD.REQ_API.request({
			httpMethod: "POST",
			method: "companies/list",
			data: {},
		})) as IAPIResponse<ICompanyRaw[]>;
	};

	private getCompany: (id: string) => Promise<IAPIResponse<ICompanyRaw>> = async id => {
		if (!id) return { data: null as unknown as ICompanyRaw };

		const res = await GD.REQ_API.request({
			httpMethod: "GET",
			method: `companies/get?uid=${id}`,
		});
		return res as IAPIResponse<ICompanyRaw>;
	};

	private getCompanyTemplate: () => Promise<IAPIResponse<ICompanyRaw>> = async () => {
		const res = await GD.REQ_API.request({
			httpMethod: "GET",
			method: "companies/get?uid=-1",
		});
		return res as IAPIResponse<ICompanyRaw>;
	};

	private registerCompany: (data: ICompanyRaw) => Promise<IAPIResponse<ICompanyRaw>> = async data => {
		const res = await GD.REQ_API.request({
			method: "companies/register",
			httpMethod: "POST",
			data,
		});

		if (res.error) return res;
		const companies = await this.getCompanies();
		if (companies.data) GD.S_COMPANY_LIST_UPDATED.invoke(companies.data);
		return res as IAPIResponse<ICompanyRaw>;
	};

	private updateCompany: (data: ICompanyRaw) => Promise<IAPIResponse<IStatusResponse>> = async data => {
		const res = (await GD.REQ_API.request({
			method: "companies/update",
			httpMethod: "POST",
			data,
		})) as IAPIResponse<IStatusResponse>;

		if (res.data?.status === true) {
			const companies = await this.getCompanies();
			if (companies.data) GD.S_COMPANY_LIST_UPDATED.invoke(companies.data);
			return { data: res.data };
		}
		return { error: { code: -1, message: "Unknown error" } };
	};
}
